import {
  Ballpark,
  Ball,
  BallMode,
  BallSnapshot,
  ReplicationEvent,
} from '@deepwake/shared';

export interface BallDeco {
  kind: string;
  name?: string;
  shipClass?: string;
  faction?: string;
  charId?: number;
  toSystem?: number;
}

/**
 * Client-side mirror of the server park. Runs the identical Destiny
 * integrator: on every delta we first evolve one tick (bit-identical to the
 * server for undisturbed balls), then apply the tick's events as new initial
 * conditions — exactly Destiny's client ticker model.
 */
export class ClientSim {
  park = new Ballpark();
  deco = new Map<number, BallDeco>();
  myBallId: number | null = null;
  tick = 0;
  /** Wall-clock ms when the last delta landed (drives render interpolation). */
  lastDeltaAt = performance.now();

  constructor() {
    this.park.isMaster = false;
  }

  reset(): void {
    this.park = new Ballpark();
    this.park.isMaster = false;
    this.deco.clear();
  }

  applyFullstate(balls: BallSnapshot[], tick: number, yourBallId: number | null): void {
    this.reset();
    for (const s of balls) this.applySnapshot(s, true);
    this.wireFollows(balls);
    this.tick = tick;
    this.myBallId = yourBallId;
    this.lastDeltaAt = performance.now();
  }

  applyDelta(events: ReplicationEvent[], tick: number): void {
    // 1. Advance local sim to the server's tick (deterministic extrapolation)
    this.park.evolve();
    this.tick = tick;
    this.lastDeltaAt = performance.now();

    // 2. Apply this tick's events as new initial conditions
    const snaps: BallSnapshot[] = [];
    for (const ev of events) {
      if (ev.e === 'enter' || ev.e === 'mode') {
        this.applySnapshot(ev.ball, ev.e === 'enter');
        snaps.push(ev.ball);
      } else if (ev.e === 'exit') {
        this.park.removeBall(ev.ballId);
        this.deco.delete(ev.ballId);
      }
    }
    this.wireFollows(snaps);
  }

  private applySnapshot(s: BallSnapshot, isNew: boolean): void {
    let ball = this.park.balls.get(s.id);
    if (!ball) {
      ball = this.park.addBall({
        id: s.id,
        mass: s.mass,
        radius: s.radius,
        maxVel: s.maxVel,
        agility: s.agility,
        speedFraction: s.speedFraction,
        isFree: s.isFree,
        isGlobal: s.isGlobal,
        isMassive: s.isMassive,
        x: s.x,
        y: s.y,
        z: s.z,
        vx: s.vx,
        vy: s.vy,
        vz: s.vz,
      });
      ball.newYaw = ball.oldYaw = s.yaw;
      ball.newPitch = ball.oldPitch = s.pitch;
      ball.newRoll = ball.oldRoll = s.roll;
    } else {
      // Keep previous newPos as oldPos so render interpolation stays smooth
      ball.oldPos.copy(ball.newPos);
      ball.oldVel.copy(ball.newVel);
      ball.newPos.set(s.x, s.y, s.z);
      ball.newVel.set(s.vx, s.vy, s.vz);
      ball.mass = s.mass;
      this.park.setBallRadius(s.id, s.radius);
      this.park.setBallMaxVel(s.id, s.maxVel);
      this.park.setBallAgility(s.id, s.agility);
      this.park.setSpeedFraction(s.id, s.speedFraction);
      ball.isMassive = s.isMassive;
      if (s.isFree && !ball.isFree) this.park.setBallFree(s.id, true);
    }
    ball.mode = s.mode;
    ball.goto.set(s.gx, s.gy, s.gz);
    ball.followId = s.followId;
    ball.followRange = s.followRange;
    ball.ownerId = s.ownerId;
    ball.effectStamp = s.effectStamp;
    ball.lastCollision = s.lastCollision;
    ball.warpMinRange = s.warpMinRange;
    ball.formationId = s.formationId;

    this.deco.set(s.id, {
      kind: s.kind,
      name: s.name,
      shipClass: s.shipClass,
      faction: s.faction,
      charId: s.charId,
      toSystem: (s as BallSnapshot & { toSystem?: number }).toSystem,
    });
  }

  /** Resolve followPtr once all balls of a batch exist. */
  private wireFollows(snaps: BallSnapshot[]): void {
    for (const s of snaps) {
      const ball = this.park.balls.get(s.id);
      if (!ball) continue;
      if (
        (ball.mode === BallMode.FOLLOW ||
          ball.mode === BallMode.ORBIT ||
          ball.mode === BallMode.MISSILE ||
          ball.mode === BallMode.FORMATION) &&
        ball.followId
      ) {
        const target = this.park.balls.get(ball.followId);
        if (target) {
          ball.followPtr = target;
          target.followers.add(ball.id);
        } else {
          // Target outside our bubble: fall back to a goto toward its last spot
          ball.followPtr = null;
          ball.mode = BallMode.GOTO;
        }
      }
    }
  }

  myBall(): Ball | null {
    return this.myBallId !== null ? (this.park.balls.get(this.myBallId) ?? null) : null;
  }

  /** 0..1 interpolation factor within the current tick, for rendering. */
  alpha(now: number): number {
    return Math.min(1, (now - this.lastDeltaAt) / 1000);
  }
}
